/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/pad-focus-helpers.ts
 * FILE_ID: QB-AGGRID-PAD-FOCUS-001
 * PURPOSE: Pure I→O helpers for SAM-style "after pad promotion, focus
 *          the next blank row" behaviour. Separates the (testable)
 *          row-array math from the (untestable) AG-Grid imperative
 *          setFocusedCell call.
 * WHY: When the user types into the first pad and hits Enter, AG-Grid's
 *      native Enter advances focus to the NEXT row by id (e.g.
 *      "__pad__1"). But after the backend round-trip + refetch inserts
 *      a new real row above the pads, "__pad__1" is now the SECOND pad,
 *      and the user expects to land on the FIRST pad (the row directly
 *      below their newly-created real line item). This helper finds
 *      that target index from the row array.
 * RELATED:
 *   - usePadFocusAdvance.ts — hook that wires this into AG-Grid api
 *   - pad-row-helpers.ts — isPadRowId / pad id contract
 */

import type { QuoteLineItem } from '@/lib/quoteApi'
import { isPadRowId } from './pad-row-helpers'

/**
 * Find the index of the first pad row in a display-rows array.
 *
 * SAM's "next blank row" is always the topmost pad — pads are always
 * the trailing block (real rows first, then N pads), so the first pad
 * is at index `realRowCount`.
 *
 * Returns -1 when no pads exist (read-only quotes, or empty display).
 */
export function findFirstPadRowIndex(rows: QuoteLineItem[]): number {
  for (let i = 0; i < rows.length; i++) {
    if (isPadRowId(rows[i]?.id)) return i
  }
  return -1
}
